import { Component, ErrorInfo, ReactNode } from 'react';

/**
 * One page's crash stays one page's crash.
 *
 * React unmounts the ENTIRE tree when a render throws, so a single malformed row —
 * an object where a card expected a sentence, a null where a list expected an array —
 * used to replace the whole admin with a white screen. And because the row is
 * persisted, refreshing reproduced it exactly: no nav, no error text, no way back.
 *
 * This catches it at the route boundary. The nav and the sidebar survive, the page
 * says what broke, and — importantly — you can still navigate somewhere else and keep
 * working while it's being fixed.
 */
interface State {
  error: Error | null;
}

export default class PageErrorBoundary extends Component<
  { children: ReactNode; resetKey?: string },
  State
> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // The stack is the only thing that makes a minified production error diagnosable.
    console.error('Page crashed:', error, info.componentStack);
  }

  componentDidUpdate(prev: { children: ReactNode; resetKey?: string }) {
    // Navigating away is the user's escape hatch, so clear the error when the route
    // changes — otherwise the boundary would pin them to the broken page.
    if (this.state.error && prev.resetKey !== this.props.resetKey)
      this.setState({ error: null });
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="p-6">
        <h2 className="text-base font-semibold text-red-800 dark:text-red-300">
          This page hit an error and stopped rendering.
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
          The rest of the admin still works - use the nav to go somewhere else. The
          details below are what to report.
        </p>
        <pre className="mt-3 text-[11px] whitespace-pre-wrap break-words bg-gray-900 text-gray-100 rounded p-3 max-h-80 overflow-y-auto">
          {this.state.error.message}
          {this.state.error.stack ? `\n\n${this.state.error.stack}` : ''}
        </pre>
        <button
          onClick={() => this.setState({ error: null })}
          className="mt-3 text-xs px-3 py-1.5 rounded bg-indigo-600 text-white hover:bg-indigo-700"
        >
          Try again
        </button>
      </div>
    );
  }
}
